import React from 'react';
import {
  buildDateRange,
  buildNumericRange,
  buildSearchParameterManager,
  SearchEngine,
  SearchParameters,
  validateRelativeDate,
} from '@coveo/headless';
import { RangeValueRequest } from '@coveo/headless/dist/definitions/features/facets/range-facets/generic/interfaces/range-facet';
// import {
//   API_DATE_FORMAT,
//   isSearchApiDate,
//   validateAbsoluteDate,
// } from '@coveo/headless/dist/definitions/api/search/date/date-format';

type SearchParameterKey = keyof SearchParameters;
type UnknownObject = { [field: string]: unknown[] };

/**
 * Search parameters should not be restored until all components are registered.
 * Additionally, a search should not be executed until search parameters are restored.
 *
 * Usage:
 * import { bindSearchParametersToURI } from './search-parameter-manager';
 * in Search page --> useEffect
 * **bindSearchParametersToURI(headlessEngineSubscriptions, aqString);
 * **const { updateAdvancedSearchQueries } = loadAdvancedSearchQueryActions(headlessEngineSubscriptions);
 * **headlessEngineSubscriptions.dispatch(updateAdvancedSearchQueries({ aq: aqString }));
 */
export function bindSearchParametersToURI(engine: SearchEngine, aq: string) {
  const delimiter = '&';
  const equal = '=';
  const rangeDelimiterExclusive = '..';
  const rangeDelimiterInclusive = '...';

  const hash = window.location.href;
  const parameters = deserialize(decodeURIComponent(hash));

  const searchParameterManager = buildSearchParameterManager(engine, {
    initialState: { parameters },
  });

  searchParameterManager.subscribe(() => {
    const url = serialize(searchParameterManager.state.parameters);
    history.pushState(null, document.title, url);
  });

  window.addEventListener('hashchange', () => {
    const hash = window.location.href;
    const parameters = deserialize(decodeURIComponent(hash));

    searchParameterManager.synchronize(parameters);
  });

  function serialize(obj: SearchParameters) {
    let hash = Object.entries(obj)
      .map(serializePair)
      .filter((str) => !str.startsWith('aq'))
      .join(delimiter);

    hash = hash && `#${hash}`;

    return `${window.location.pathname}${hash}`;
  }

  function serializePair(pair: [string, unknown]) {
    const [key, val] = pair;

    if (!isValidKey(key)) {
      return '';
    }

    if (isSpecificFacetKey(key)) {
      return isFacetObject(val) ? serializeFacets(key, val) : '';
    }

    if (key === 'nf' || key === 'df') {
      return isRangeFacetObject(val) ? serializeRangeFacets(key, val) : '';
    }

    return `${key}${equal}${encodeURIComponent(val as string | number | boolean)}`;
  }

  function isFacetObject(obj: unknown): obj is Record<string, string[]> {
    if (!isObject(obj)) {
      return false;
    }

    const isValidValue = (v: unknown) => typeof v === 'string';
    return allEntriesAreValid(obj, isValidValue);
  }

  function isRangeFacetObject(obj: unknown): obj is Record<string, RangeValueRequest[]> {
    if (!isObject(obj)) {
      return false;
    }

    const isRangeValue = (v: unknown) => isObject(v) && 'start' in v && 'end' in v;
    return allEntriesAreValid(obj, isRangeValue);
  }

  function isObject(obj: unknown): obj is object {
    return obj && typeof obj === 'object' ? true : false;
  }

  function allEntriesAreValid(obj: object, isValidValue: (v: unknown) => boolean) {
    const invalidEntries = Object.entries(obj).filter((entry) => {
      const values = entry[1];
      return !Array.isArray(values) || !values.every(isValidValue);
    });

    return invalidEntries.length === 0;
  }

  function serializeFacets(key: string, facets: Record<string, string[]>) {
    return Object.entries(facets)
      .map(
        ([facetId, values]) =>
          `${key}[${facetId}${equal}${values.map((value) => encodeURIComponent(value)).join(',')}]`
      )
      .join(delimiter);
  }

  function serializeRangeFacets(key: string, facets: Record<string, RangeValueRequest[]>) {
    return Object.entries(facets)
      .map(([facetId, ranges]) => {
        const value = ranges
          .map(
            ({ start, end, endInclusive }) =>
              `${start}${endInclusive ? rangeDelimiterInclusive : rangeDelimiterExclusive}${end}`
          )
          .join(',');
        return `${key}[${facetId}${equal}${value}]`;
      })
      .join(delimiter);
  }

  function deserialize(fragment: any): SearchParameters {
    const parts = fragment.split(delimiter);
    const keyValuePairs = parts
      .map((part: string) => splitOnFirstEqual(part))
      .map(preprocessObjectPairs)
      .filter(isValidPair)
      .map(cast);

    return keyValuePairs.reduce((acc: SearchParameters, pair: [any, any]) => {
      const [key, val] = pair;

      if (keyHasObjectValue(key)) {
        const mergedValues = { ...acc[key], ...(val as object) };
        return { ...acc, [key]: mergedValues };
      }

      return { ...acc, [key]: val };
    }, {});
  }

  function splitOnFirstEqual(str: string) {
    const [first, ...rest] = str.split(equal);
    const second = rest.join(equal);

    return [first, second];
  }

  function preprocessObjectPairs(pair: string[]) {
    const [key, val] = pair;
    const objectKey = /^(f|fExcluded|cf|nf|df|sf|af)\[(.+)\]$/;
    const result = objectKey.exec(key);

    if (!result) {
      return pair;
    }

    const paramKey = result[1];
    const id = result[2];
    const values = val.split(',');
    const processedValues = processObjectValues(paramKey, values);
    const obj = { [id]: processedValues };

    return [paramKey, JSON.stringify(obj)];
  }

  function processObjectValues(key: string, values: string[]) {
    if (key === 'nf') {
      return buildNumericRanges(values);
    }

    if (key === 'df') {
      return buildDateRanges(values);
    }

    return values;
  }

  function buildNumericRanges(ranges: string[]) {
    return ranges
      .map((str) => {
        const { startAsString, endAsString, isEndInclusive } =
          splitRangeValueAsStringByDelimiter(str);

        return {
          start: parseFloat(startAsString),
          end: parseFloat(endAsString),
          endInclusive: isEndInclusive,
        };
      })
      .filter(({ start, end }) => Number.isFinite(start) && Number.isFinite(end))
      .map(({ start, end, endInclusive }) =>
        buildNumericRange({ start, end, state: 'selected', endInclusive })
      );
  }

  function isValidDateRangeValue(date: string) {
    try {
      // TODO
      // if (isSearchApiDate(date)) {
      //   validateAbsoluteDate(date, API_DATE_FORMAT);
      //   return true;
      // }
      if (isRelativeDateFormat(date)) {
        validateRelativeDate(date);
        return true;
      }

      return false;
    } catch (error) {
      return false;
    }
  }

  function splitDate(date: string) {
    return date.toLocaleLowerCase().split('-');
  }

  function isRelativeDateFormat(date: string) {
    const validRelativeDatePeriods = ['past', 'now', 'next'];

    const validRelativeDateUnits = ['minute', 'hour', 'day', 'week', 'month', 'quarter', 'year'];

    const [period, amount, unit] = splitDate(date);
    if (period === 'now') {
      return true;
    }

    if (!validRelativeDatePeriods.includes(period)) {
      return false;
    }

    if (!validRelativeDateUnits.includes(unit)) {
      return false;
    }

    const intAmount = parseInt(amount);
    if (isNaN(intAmount) || intAmount <= 0) {
      return false;
    }

    return true;
  }

  function buildDateRanges(ranges: string[]) {
    return ranges
      .map((str) => {
        const { isEndInclusive, startAsString, endAsString } =
          splitRangeValueAsStringByDelimiter(str);

        return {
          start: startAsString,
          end: endAsString,
          endInclusive: isEndInclusive,
        };
      })
      .filter(({ start, end }) => isValidDateRangeValue(start) && isValidDateRangeValue(end))
      .map(({ start, end, endInclusive }) =>
        buildDateRange({ start, end, state: 'selected', endInclusive })
      );
  }

  function isValidPair<K extends SearchParameterKey>(pair: string[]): any {
    const validKey = isValidKey(pair[0]);
    const lengthOfTwo = pair.length === 2;
    return validKey && lengthOfTwo;
  }

  function isValidKey(key: string): any {
    const supportedParameters: any = {
      q: true,
      aq: true,
      cq: true,
      enableQuerySyntax: true,
      firstResult: true,
      numberOfResults: true,
      sortCriteria: true,
      f: true,
      fExcluded: true,
      cf: true,
      nf: true,
      df: true,
      debug: true,
      sf: true,
      tab: true,
      af: true,
    };

    return key in supportedParameters;
  }

  function cast<K extends SearchParameterKey>(pair: [K, string]): [K, unknown] {
    const [key, value] = pair;

    if (key === 'enableQuerySyntax') {
      return [key, value === 'true'];
    }

    if (key === 'debug') {
      return [key, value === 'true'];
    }

    if (key === 'firstResult') {
      return [key, parseInt(value)];
    }

    if (key === 'numberOfResults') {
      return [key, parseInt(value)];
    }

    if (keyHasObjectValue(key)) {
      return [key, castUnknownObject(value)];
    }

    return [key, decodeURIComponent(value)];
  }

  function castUnknownObject(value: string) {
    const jsonParsed: UnknownObject = JSON.parse(value);
    const ret: UnknownObject = {};
    Object.entries(jsonParsed).forEach((entry) => {
      const [id, values] = entry;
      ret[id] = values.map((v: any) => (typeof v === 'string' ? decodeURIComponent(v) : v));
    });

    return ret;
  }

  function keyHasObjectValue(
    key: any
  ): key is 'f' | 'af' | 'cf' | 'df' | 'nf' | 'sf' | 'fExcluded' {
    const keys = ['f', 'fExcluded', 'cf', 'nf', 'df', 'sf', 'af'];
    return keys.includes(key);
  }

  function isSpecificFacetKey(key: any): key is 'f' | 'af' | 'cf' | 'sf' | 'fExcluded' {
    const keys = ['f', 'af', 'cf', 'sf', 'fExcluded'];
    return keys.includes(key);
  }

  function splitRangeValueAsStringByDelimiter(str: string) {
    const isEndInclusive = str.indexOf(rangeDelimiterInclusive) !== -1;
    const [startAsString, endAsString] = str.split(
      isEndInclusive ? rangeDelimiterInclusive : rangeDelimiterExclusive
    );
    return {
      isEndInclusive,
      startAsString,
      endAsString,
    };
  }

  return {
    autoUpdateURI: () =>
      searchParameterManager.subscribe(() => {
        window.location.hash = serialize(searchParameterManager.state.parameters);
      }),
  };
}

export default bindSearchParametersToURI;
